/**
 * API 类型定义
 * 统一管理所有 API 相关的 TypeScript 类型定义
 */

// ==================== 订单相关类型 ====================

export interface Order {
  id: string;
  time: string;
  status: "win" | "lose" | "other";
  team: string;
  betType: string;
  match: string;
  event: string;
  betTime: string;
  odds: string;
  wager: number;
  return: number;
  pickId: string;
}

export interface OrderListParams {
  tab?: "unsettled" | "settled";
  filter?: "all" | "win" | "lose" | "other";
  page?: number;
  pageSize?: number;
}

export interface OrderListResponse {
  list: Order[];
  total: number;
  page: number;
  pageSize: number;
}

// ==================== 授权认证相关类型 ====================

export interface OAuthLoginParams {
  session_ticket: string;
  basic: Record<string, unknown>;
}

/** 登录响应中的 userinfo */
export interface OAuthLoginUserinfo {
  user_id: number;
  business_user_id: string;
  game_currency_alias: string;
  currency_type: string;
  exchange_rate: number;
}

/** 登录响应中的 basic */
export interface OAuthLoginBasic {
  ab_test?: string | null;
  stats_sdk_version?: string | null;
  sdk_version_code?: number | null;
  sdk_version_name?: string | null;
  sz_id?: string | null;
  sn_language?: string | null;
  sn_country?: string | null;
  hot_version?: number | null;
}

/** 登录响应中的 account_info */
export interface OAuthLoginAccountInfo {
  create_time?: number | string | null;
  role?: string | null;
  birthday?: string | null;
  email?: string | null;
  phone?: string | null;
  area_code?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  vip_level?: number | null;
  register_time?: number | string | null;
  adgroup?: string | null;
  adset?: string | null;
  game_currency_alias?: string | null;
  currency_type?: string | null;
  exchange_rate?: number | null;
  business_id?: number;
  product_id?: number;
  business_user_id?: string;
  business_user_token?: string;
  sdk_id?: number;
  [key: string]: unknown;
}

/** 登录响应中的 connection_info */
export interface OAuthLoginConnectionInfo {
  user_agent?: string;
  device_id?: string;
  ip?: string;
  country?: string;
  state?: string;
  zip_code?: string;
  media_source?: string;
  campaign?: string;
  package_name?: string;
  version_code?: number;
  mobile_app_version?: string;
  mobile_device_brand?: string;
  sm_id?: string;
  os?: string;
  language?: string;
  initial_country?: string;
  hot_version_code?: number;
  channel?: string;
  user_channel?: string;
  sn_support_language?: string;
  android_id?: string;
  google_aid?: string;
  net?: string;
  install_days?: number;
  install_time?: number | string | null;
  dpi?: number;
  screen_width?: number;
  screen_height?: number;
  trace_id?: string;
  [key: string]: unknown;
}

/** 登录接口返回的 data 结构 */
export interface OAuthLoginData {
  user_id: number;
  business_id: number;
  product_id: number;
  access_token: string;
  expires_time?: number;
  refresh_token: string;
  userinfo?: OAuthLoginUserinfo;
  basic?: OAuthLoginBasic;
  account_info?: OAuthLoginAccountInfo;
  connection_info?: OAuthLoginConnectionInfo;
}

/** 持久化的登录数据（与 /login 返回的 data 中对应字段一致，用于 store 单一份 loginData） */
export interface PersistedLoginData {
  userinfo?: OAuthLoginUserinfo | null;
  account_info?: OAuthLoginAccountInfo | null;
  connection_info?: OAuthLoginConnectionInfo | null;
  basic?: OAuthLoginBasic | null;
}

/** 登录接口完整响应（code 可能为 0 或 200 表示成功） */
export interface OAuthLoginResponse {
  code: number;
  timestamp?: number;
  message?: string;
  data: OAuthLoginData;
  etag?: string;
  uri?: string;
  trace_id?: string;
}

// ==================== 钱包相关类型 ====================

export interface WalletInfoUsd {
  /** sc 余额（现金） */
  cash: number | null;
  /** gc 余额（金币） */
  coin: number | null;
  /** 免费投注余额 */
  free_bet: number | null;
}

export interface WalletBalanceData {
  /** 货币类型 */
  currency_type: string;
  /** 汇率 */
  exchange_rate: number;
  /** 用户ID */
  user_id: number;
  /** 钱包信息（USD） */
  wallet_info_usd: WalletInfoUsd;
}

// ==================== Betby 相关类型 ====================

export interface BetbyInitInfo {
  /** 货币类型（统一使用小写：gc, sc） */
  money_type: "gc" | "sc";
  /** 品牌ID */
  brand_id: string;
  /** Betby Token */
  bet_by_token: string;
  /** 语言 */
  lang: string;
  /** 主题名称 */
  theme_name: string;
}

export interface BetbyInitInfoData {
  /** SDK ID */
  sdk_id: number;
  /** Betby 初始化信息列表 */
  bet_by_init_info: BetbyInitInfo[];
}

// ==================== 红点相关类型 ====================

export interface RedPointBetOrderUpdate {
  /** SC 订单数据 */
  order_data: string[];
  /** GC 订单数据 */
  gc_order_data: string[];
}

export interface RedPointMsg {
  /** 订单更新红点信息 */
  bet_order_update: RedPointBetOrderUpdate;
}

export interface RedPointData {
  /** 消息ID */
  id: string;
  /** 用户ID */
  user_id: number;
  /** 消息内容 */
  msg: RedPointMsg;
  /** 创建时间 */
  create_time: number;
}

/** 移除红点请求参数 */
export interface RedPointReduceRequest {
  /** 红点类型 */
  type: string;
  /** 消息类型 */
  msg_type: string;
}

// ==================== 投注记录相关类型 ====================

/** 投注结果类型 */
export type BetResultType = 1 | 2;
/** 1: 未结算 (unsettled) */
/** 2: 已结算 (settled) */

/** 主订单结算子状态（原始数值类型） */
export type BetOrderSubStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6 | null;

/** 主订单结算子状态枚举：
 * 0 / null: 无子状态
 * 1: WIN（赢）
 * 2: LOST（输）
 * 3: REFUND（退还）
 * 4: CANCELED（取消）
 * 5: HALF_WIN（半赢）
 * 6: HALF_LOST（半输）
 */
export enum BetOrderSubStatusEnum {
  NONE = 0,
  WIN = 1,
  LOST = 2,
  REFUND = 3,
  CANCELED = 4,
  HALF_WIN = 5,
  HALF_LOST = 6,
}

/** 投注 Tab 类型 */
export type BetTabType = 1 | 2 | 3 | 4 | 5;
/** 1: 审核中 (reviewing) */
/** 2: 待结算 (pending settlement) */
/** 3: 胜利 (win) */
/** 4: 失败 (loss) */
/** 5: 其他 (other) */

/** 游戏货币类型 */
export enum GameCurrencyType {
  /** 1: coin (gc余额) */
  COIN = 1,
  /** 2: cash (sc余额) */
  CASH = 2,
  /** 3: free_bet (免费投注) */
  FREE_BET = 3,
}
export enum MidPromotionCurrencyType {
  /** 1000: free_money (免费投注) */
  FREE_MONEY = 1000,
  /** 1001: bet_refund (投注退款) */
  BET_REFUND = 1001,
  /** 1002: no_risk_bet (无风险投注) */
  NO_RISK_BET = 1002,
  /** 1003: combo_boost (组合加成) */
  COMBO_BOOST = 1003,
}

/** 主订单状态枚举，避免魔法数字：
 * 100: 下注成功待审核 (Order Reviewing)
 * 101: 审核通过待结算 (Awaiting Game Result)
 * 200: 胜利 (WIN)
 * 300: 失败 (LOSE)
 * 400: 退还 (PUSH)
 * 500: 取消 (CANCELLED)
 * 600: 审核不通过 (Review Not Passed)
 */
export enum BetOrderStatusEnum {
  ORDER_REVIEWING = 100,
  AWAITING_GAME_RESULT = 101,
  WIN = 200,
  LOSE = 300,
  PUSH = 400,
  CANCELLED = 500,
  REVIEW_NOT_PASSED = 600,
}

/** 获取投注记录请求参数 */
export interface BetOrderRecordListParams {
  /** 页码 */
  page: number;
  /** 每页数量 */
  page_size: number;
  /** 投注结果类型：1=未结算, 2=已结算，必填 */
  bet_result_type: BetResultType;
  /** 投注 Tab 类型：1=审核中, 2=待结算, 3=胜利, 4=失败, 5=其他，可选 */
  bet_tab_type?: BetTabType;
  /** 游戏货币类型集合：1=coin(gc余额), 2=cash(sc余额), 3=free_bet，必填且不能为空集合 */
  game_currency_type_set: GameCurrencyType[];
}

// ==================== 投注记录 record/list 响应类型 ====================

/**
 * 注单投注方式（与 Betby 映射一致）
 * 1=SINGLE(单注) | 2=COMBO(组合投注) | 1001=SYSTEM(系统投注) | 1002=TRIXIE(三重彩) | 1003=PATENT(专利) | 1004=YANKEE(扬基) | 1005=LUCKY_15(幸运15) | 1006=CANADIAN(加拿大) | 1007=LUCKY_31(幸运31) | 1008=HEINZ(海因茨) | 1009=LUCKY_63(幸运63) | 1010=SUPER_HEINZ(超级海因茨) | 1011=GOLIATH(巨人)
 */
export type BetWay =
  | 1    // SINGLE 单注
  | 2    // COMBO 组合投注
  | 1001 // SYSTEM 系统投注 N/M
  | 1002 // TRIXIE 三重彩系统 F/3
  | 1003 // PATENT 专利系统 FS/3
  | 1004 // YANKEE 扬基系统 F/4
  | 1005 // LUCKY_15 幸运15 FS/4
  | 1006 // CANADIAN 加拿大 F/5
  | 1007 // LUCKY_31 幸运31 FS/5
  | 1008 // HEINZ 海因茨 F/6
  | 1009 // LUCKY_63 幸运63 FS/6
  | 1010 // SUPER_HEINZ 超级海因茨 F/7
  | 1011; // GOLIATH 巨人 F/8

/** 订单投注信息（投注金额拆分） */
export interface BetInfoVO {
  /** 投注 coin 数量（gc） */
  bet_coin: number | null;
  /** 投注 cash 金额（sc） */
  bet_cash: number | null;
  /** 投注 free_bet 金额 */
  bet_free_bet: number | null;
}

/** 订单结算奖励信息 */
export interface BetRewardInfoVO {
  /** 奖励 coin 数量（gc） */
  reward_coin: number | null;
  /** 奖励 cash 金额（sc） */
  reward_cash: number | null;
  /** 奖励 free_bet 金额 */
  reward_free_bet: number | null;
}

/** 订单额外奖励信息（例如活动加赠） */
export interface BetExtendRewardInfoVO {
  /** 额外奖励 coin 数量（gc） */
  extend_reward_coin: number | null;
  /** 额外奖励 cash 金额（sc） */
  extend_reward_cash: number | null;
  /** comboBoost 额外赢取的比例 */
  combo_boost_proportion: string | null;
}

/** 投注市场类型
 * 1 - 常规市场
 * 2 - feature/outright 市场
 * 3 - 赛马/狗/马车等赛场市场
 */
export type MarketType = 1 | 2 | 3;

/** 单个投注项（子订单） */
export interface BetItemVO {
  bet_order_sub_status: BetOrderSubStatus;
  /** 投注订单 id（单场时为父订单 id，串关时为各子订单 id） */
  order_id: string;
  /** 投注选项名称（例如球队名） */
  outcome_name: string;
  /** 投注市场名称（如 Moneyline） */
  market_name: string;
  /** 投注市场类型：1=常规市场，2=feature/outright，3=赛马/狗/马车市场；旧数据可能不存在 */
  market_type?: MarketType;
  /** 比赛名称 */
  league_title: string;
  /** 联赛展示名称 */
  league_show_name: string;
  /** 比赛开赛时间（毫秒时间戳） */
  scheduled: number;
  /** 赔率（美式赔率） */
  odds: number;
  /** 赔率（欧式赔率，小数） */
  decimal_odds: number;
  /** 投注项当前状态 */
  bet_item_status: number;
  /** 结算时间（毫秒时间戳），未结算为 null */
  settle_time: number | null;
}

/** record/list 接口中 data 数组的单条记录（NormalBetOrderVO） */
export interface NormalBetOrderVO {
  /** 投注订单 id */
  order_id: string;
  /** 投注货币类型：1=coin(gc)，2=cash(sc)，3=free_bet */
  game_currency_type: GameCurrencyType;
  /** 投注方式：1=Straight(单场投注)，2=Parlay(串关投注) ...等 */
  bet_way: BetWay;

  bet_way_name: string;
  /** 中台促销，可能为空 */
  mid_promotion_bet_info: {
    mid_promotion_currency_type: MidPromotionCurrencyType;
    bet_promotion_amount: number;
  } | null;
  /** 美式赔率（总赔付倍率） */
  payout: number;
  /** 欧式赔率（总赔付倍率，小数） */
  decimal_payout: number;
  /** 下单时间（毫秒时间戳） */
  bet_time: number;
  /** 结算时间（毫秒时间戳），未结算为 null */
  settle_time: number | null;
  /** 主订单结算子状态 */
  bet_order_sub_status: BetOrderSubStatus;
  /** 用户投注当前状态（101 等枚举值） */
  bet_order_status: 100 | 101 | 200 | 300 | 400 | 500 | 600;
  /** 订单级别取消原因错误码，可为空 */
  reason_code: string | null;
  /** 投注项列表 */
  bet_items: BetItemVO[];
  /** 投注金额信息 */
  bet_info: BetInfoVO;
  /** 结算奖励信息 */
  reward_info: BetRewardInfoVO;
  /** 额外奖励信息，可能为空 */
  extend_reward_info: BetExtendRewardInfoVO | null;
}

/**
 * 获取投注记录响应 data 字段类型
 * 对应接口返回的 data: NormalBetOrderVO[]
 */
export type BetOrderRecordListData = NormalBetOrderVO[];

// ==================== Unity 消息相关类型 ====================

/** Unity 消息类型 */
export type UnityMessageType =
  // 返回按钮点击 / token 过期：统一 LoggedOut
  | "LoggedOut"
  // 充值操作触发
  | "TurnShop"
  // 登录操作触发
  | "SDKLogin"
  // 货币切换
  | "MoneyType"
  // 通知业务层刷新钱包
  | "GetWallet"
  // SDK 初始化完成（成功或失败）
  | "SDKReady";

/** Unity 消息数据格式（既支持对象也支持数组） */
export type UnityMessageData = Record<string, unknown> | unknown[];

/** Unity 消息对象 */
export interface UnityMessage {
  /** 消息类型 (key/type) */
  type: UnityMessageType;
  /** 数据格式 */
  data: UnityMessageData;
}

// ==================== 系统配置相关类型 ====================

export interface SystemConfigData {
  system_time: string;
}

// ==================== 客户端 IP 相关类型 ====================

export interface ClientIpData {
  /** 客户端真实 IP */
  client_ip: string;
  /** 是否内网 IP */
  inner_ip: boolean;
  /** 国家/地区码（例如 US） */
  country_code: string;
  /** 国家/地区名称 */
  country_name: string;
  /** 州/省编码 */
  state_code: string | null;
}
